import { useState, type FormEvent } from "react";
import { ArrowIcon } from "./Icons";
import { FormProtectionWidget, SalesPitchNotice, VERIFY_FIRST, useFormProtection } from "./FormProtection";
import { EMAIL, PHONE_DISPLAY, PHONE_HREF } from "../site";

type ContactFormProps = {
  kind?: "general" | "wedding";
};

type SubmissionState =
  | { phase: "idle" }
  | { phase: "submitting" }
  | { phase: "success"; message: string }
  | { phase: "error"; message: string };

function value(form: FormData, key: string): string {
  return String(form.get(key) ?? "").trim();
}

export function ContactForm({ kind = "general" }: ContactFormProps) {
  const [submission, setSubmission] = useState<SubmissionState>({ phase: "idle" });
  const isWedding = kind === "wedding";
  const protection = useFormProtection();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);

    const payload = {
      kind,
      name: value(form, "name"),
      email: value(form, "email"),
      phone: value(form, "phone"),
      service: value(form, "service"),
      message: value(form, "message"),
      sourcePath: window.location.pathname,
      company: value(form, "company"),
      ...(isWedding
        ? {
            eventDetails: {
              partyName: value(form, "partyName"),
              eventDate: value(form, "eventDate"),
              partySize: value(form, "partySize"),
              location: value(form, "location"),
              makeup: form.get("makeup") === "on",
              waxing: form.get("waxing") === "on",
              flowerGirl: form.get("flowerGirl") === "on",
            },
          }
        : {}),
    };

    const requestBody = JSON.stringify(payload);
    // With verification on, nothing is sent until the check gives a token.
    const protectionHeaders = protection.headers(requestBody);
    if (!protectionHeaders) {
      setSubmission({ phase: "error", message: VERIFY_FIRST });
      return;
    }
    setSubmission({ phase: "submitting" });

    try {
      let response: Response;
      try {
        response = await fetch("/api/contact", {
          method: "POST",
          headers: { "content-type": "application/json", ...protectionHeaders },
          body: requestBody,
        });
      } finally {
        protection.sent();
      }
      const body = (await response.json().catch(() => null)) as { error?: string; received?: boolean } | null;
      if (!response.ok) throw new Error(body?.error || "We could not send your message.");
      protection.succeeded();
      formElement.reset();
      setSubmission({
        phase: "success",
        // A message held for review has been received but is not yet on its way.
        message: body?.received
          ? isWedding
            ? "Thank you. We received your wedding inquiry."
            : "Thank you. We received your message."
          : isWedding
            ? "Thank you. Your wedding inquiry is on its way to our special-event team."
            : "Thank you. Your message is on its way to the salon.",
      });
    } catch (error) {
      setSubmission({
        phase: "error",
        message: error instanceof Error ? error.message : "We could not send your message.",
      });
    }
  }

  return (
    <form className="contact-form" onSubmit={submit}>
      <div className="form-grid">
        <label>
          <span>Your name <b aria-hidden="true">*</b></span>
          <input name="name" autoComplete="name" maxLength={120} required />
        </label>
        <label>
          <span>Email address <b aria-hidden="true">*</b></span>
          <input name="email" type="email" autoComplete="email" maxLength={254} required />
        </label>
        <label>
          <span>Phone number</span>
          <input name="phone" type="tel" autoComplete="tel" maxLength={160} />
        </label>
        {isWedding ? (
          <label>
            <span>Bridal party name</span>
            <input name="partyName" maxLength={160} />
          </label>
        ) : (
          <label>
            <span>What can we help with?</span>
            <select name="service" defaultValue="">
              <option value="">Choose a service</option>
              <option>Haircut and styling</option>
              <option>Hair color</option>
              <option>Hair treatment</option>
              <option>Makeup</option>
              <option>Waxing</option>
              <option>Wedding or special event</option>
              <option>Something else</option>
            </select>
          </label>
        )}
      </div>

      {isWedding && (
        <>
          <div className="form-grid form-grid--three">
            <label>
              <span>Event date</span>
              <input name="eventDate" type="date" />
            </label>
            <label>
              <span>Party size</span>
              <input name="partySize" inputMode="numeric" maxLength={160} placeholder="For example, 6" />
            </label>
            <label>
              <span>Service location</span>
              <select name="location" defaultValue="">
                <option value="">Choose one</option>
                <option>At Nadia's Salon</option>
                <option>On location</option>
                <option>Not sure yet</option>
              </select>
            </label>
          </div>
          <fieldset className="interest-fieldset">
            <legend>Services you’re considering</legend>
            <label><input type="checkbox" name="makeup" /> Makeup</label>
            <label><input type="checkbox" name="waxing" /> Waxing</label>
            <label><input type="checkbox" name="flowerGirl" /> Flower girl hair</label>
          </fieldset>
        </>
      )}

      <label className="form-message">
        <span>{isWedding ? "Tell us about your day" : "How can we help?"} <b aria-hidden="true">*</b></span>
        <textarea
          name="message"
          rows={6}
          maxLength={5000}
          required
          placeholder={isWedding ? "Share your timeline, venue, looks you love, or any questions." : "Tell us what you’re looking for and any timing preferences."}
        />
      </label>

      <label className="honeypot" aria-hidden="true">
        Company
        <input name="company" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      </label>

      <FormProtectionWidget control={protection} />

      <div className="form-footer">
        <button className="button" type="submit" disabled={submission.phase === "submitting"}>
          {submission.phase === "submitting" ? "Sending…" : isWedding ? "Send wedding inquiry" : "Send message"}
          {submission.phase !== "submitting" && <ArrowIcon />}
        </button>
        <p>Prefer to talk? <a href={PHONE_HREF}>Call {PHONE_DISPLAY}</a>.</p>
      </div>
      <SalesPitchNotice />

      <div className={`form-status form-status--${submission.phase}`} aria-live="polite">
        {submission.phase === "success" && <p>{submission.message}</p>}
        {submission.phase === "error" && (
          <p>{submission.message} You can also <a href={`mailto:${EMAIL}`}>email the salon</a>.</p>
        )}
      </div>
    </form>
  );
}
